import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Printer, HardHat, Check, Undo2, History, FileText } from "lucide-react";

const mondayOf = (d) => {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day);
  return x.toISOString().slice(0, 10);
};

export default function Payroll() {
  const { lang, t } = useLang();
  const today = new Date();
  const [start, setStart] = useState(mondayOf(today));
  const [end, setEnd] = useState(() => {
    const m = new Date(mondayOf(today));
    m.setDate(m.getDate() + 6);
    return m.toISOString().slice(0, 10);
  });
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [receiptFor, setReceiptFor] = useState(null);

  const loadHistory = useCallback(() => api.get("/payroll/history").then((r) => setHistory(r.data)).catch(() => {}), []);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/payroll", { params: { start, end } });
      setData(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }, [start, end]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const markPaid = async (r) => {
    try {
      await api.post("/payroll/pay", { start: data.period.start, end: data.period.end, employee_name: r.employee_name, hours: r.hours, pay: r.pay, hourly_rate: r.hourly_rate });
      toast.success(`${r.employee_name} ${lang === "es" ? "marcado como pagado" : "marked as paid"}`);
      load(); loadHistory();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const unmarkPaid = async (r) => {
    try {
      await api.delete(`/payroll/pay/${r.payment_id}`);
      toast.success(lang === "es" ? "Pago revertido" : "Payment reverted");
      load(); loadHistory();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="payroll-page">
      <div className="no-print">
        <div className="flex items-center justify-between">
          <h1 className="font-head text-2xl font-extrabold tracking-tight text-secondary">{lang === "es" ? "Nómina semanal" : "Weekly payroll"}</h1>
          <Button variant="outline" className="rounded-sm" onClick={() => setShowHistory((s) => !s)} data-testid="payroll-history-toggle"><History className="mr-2 h-4 w-4" />{lang === "es" ? "Historial de pagos" : "Payment history"} ({history.length})</Button>
        </div>
        <p className="mt-1 text-sm text-slate-500">{lang === "es" ? "Calcula el pago de tu cuadrilla (horas × tarifa), marca quién ya cobró y expórtalo a PDF." : "Compute crew pay (hours × rate), mark who's been paid, and export to PDF."}</p>

        <div className="mt-4 flex flex-wrap items-end gap-3 rounded-sm border border-slate-200 bg-white p-4">
          <div>
            <Label className="text-xs font-bold uppercase text-slate-500">{lang === "es" ? "Desde" : "From"}</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 rounded-sm" data-testid="payroll-start" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase text-slate-500">{lang === "es" ? "Hasta" : "To"}</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 rounded-sm" data-testid="payroll-end" />
          </div>
          <Button onClick={() => window.print()} className="rounded-sm bg-secondary hover:bg-slate-700" data-testid="payroll-print-btn"><Printer className="mr-2 h-4 w-4" />{lang === "es" ? "Imprimir / PDF" : "Print / PDF"}</Button>
        </div>

        {showHistory && (
          <div className="mt-4 overflow-x-auto rounded-sm border border-slate-200 bg-white" data-testid="payroll-history">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-400">
                  <th className="p-3">{lang === "es" ? "Empleado" : "Employee"}</th>
                  <th className="p-3">{lang === "es" ? "Periodo" : "Period"}</th>
                  <th className="p-3 text-right">{lang === "es" ? "Horas" : "Hours"}</th>
                  <th className="p-3 text-right">{lang === "es" ? "Pago" : "Pay"}</th>
                  <th className="p-3">{lang === "es" ? "Pagado el" : "Paid on"}</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className="border-b border-slate-100" data-testid={`payroll-history-row-${h.id}`}>
                    <td className="p-3 font-semibold text-secondary">{h.employee_name}</td>
                    <td className="p-3 text-slate-500">{h.start} → {h.end}</td>
                    <td className="p-3 text-right text-slate-600">{h.hours}h</td>
                    <td className="p-3 text-right font-bold text-accent">${h.pay.toFixed(2)}</td>
                    <td className="p-3 text-slate-500">{new Date(h.paid_at).toLocaleDateString()}</td>
                  </tr>
                ))}
                {history.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-400">{lang === "es" ? "Aún no hay pagos registrados." : "No payments recorded yet."}</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data && (
        <div className="payroll-print mt-6 rounded-sm border border-slate-200 bg-white p-6" data-testid="payroll-report">
          <div className="flex items-center justify-between border-b border-slate-200 pb-4">
            <div className="flex items-center gap-3">
              {data.business.logo ? (
                <img src={data.business.logo} alt="logo" className="h-12 w-12 object-contain" />
              ) : (
                <span className="flex h-12 w-12 items-center justify-center rounded-sm bg-primary text-white"><HardHat className="h-6 w-6" /></span>
              )}
              <div>
                <p className="font-head text-lg font-bold text-secondary">{data.business.name}</p>
                <p className="text-xs text-slate-400">{lang === "es" ? "Reporte de nómina" : "Payroll report"}</p>
              </div>
            </div>
            <div className="text-right text-sm text-slate-500">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{lang === "es" ? "Periodo" : "Period"}</p>
              <p className="font-semibold text-secondary">{data.period.start} → {data.period.end}</p>
            </div>
          </div>

          <table className="mt-4 w-full text-sm">
            <thead className="border-b border-slate-200">
              <tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-400">
                <th className="py-2">{lang === "es" ? "Empleado" : "Employee"}</th>
                <th className="py-2 text-right">{lang === "es" ? "$/hora" : "Rate"}</th>
                <th className="py-2 text-right">{lang === "es" ? "Horas" : "Hours"}</th>
                <th className="py-2 text-right">{lang === "es" ? "Pago" : "Pay"}</th>
                <th className="py-2 text-right no-print">{lang === "es" ? "Estado" : "Status"}</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.employee_name} className="border-b border-slate-100" data-testid={`payroll-row-${r.employee_name}`}>
                  <td className="py-2.5">
                    <span className="font-semibold text-secondary">{r.employee_name}</span>
                    {r.paid && <span className="ml-2 rounded-sm bg-accent/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-accent" data-testid={`payroll-paid-badge-${r.employee_name}`}>{lang === "es" ? "Pagado" : "Paid"}</span>}
                    {r.projects.length > 0 && <span className="ml-2 text-xs text-slate-400">{r.projects.map((p) => `${p.title} (${p.hours}h)`).join(", ")}</span>}
                  </td>
                  <td className="py-2.5 text-right text-slate-500">${r.hourly_rate}</td>
                  <td className="py-2.5 text-right font-semibold text-secondary">{r.hours}h</td>
                  <td className="py-2.5 text-right font-bold text-accent">${r.pay.toFixed(2)}</td>
                  <td className="py-2.5 text-right no-print">
                    {r.paid ? (
                      <span className="inline-flex gap-1">
                        <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setReceiptFor(r)} data-testid={`payroll-receipt-${r.employee_name}`}><FileText className="mr-1 h-3.5 w-3.5" />{lang === "es" ? "Recibo" : "Receipt"}</Button>
                        <Button size="sm" variant="ghost" className="rounded-sm text-slate-500 hover:text-destructive" onClick={() => unmarkPaid(r)} data-testid={`payroll-unpay-${r.employee_name}`}><Undo2 className="mr-1 h-3.5 w-3.5" />{lang === "es" ? "Revertir" : "Undo"}</Button>
                      </span>
                    ) : (
                      <Button size="sm" className="rounded-sm bg-accent hover:bg-emerald-600" onClick={() => markPaid(r)} data-testid={`payroll-pay-${r.employee_name}`}><Check className="mr-1 h-3.5 w-3.5" />{lang === "es" ? "Pagar" : "Pay"}</Button>
                    )}
                  </td>
                </tr>
              ))}
              {data.rows.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-slate-400">{lang === "es" ? "Sin horas registradas en este periodo." : "No hours logged in this period."}</td></tr>}
            </tbody>
            {data.rows.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-slate-200 font-head font-extrabold text-secondary">
                  <td className="py-3">{lang === "es" ? "Total" : "Total"}</td>
                  <td></td>
                  <td className="py-3 text-right" data-testid="payroll-total-hours">{data.total_hours}h</td>
                  <td className="py-3 text-right text-accent" data-testid="payroll-total-pay">${data.total_pay.toFixed(2)}</td>
                  <td className="py-3 text-right text-xs font-semibold text-slate-400 no-print">{lang === "es" ? "Pagado" : "Paid"}: ${data.total_paid.toFixed(2)}</td>
                </tr>
              </tfoot>
            )}
          </table>
          <p className="mt-6 text-xs text-slate-400">{lang === "es" ? `Generado por ${t("brand")} · Basado en entradas/salidas registradas.` : `Generated by ${t("brand")} · Based on logged clock-in/out entries.`}</p>
        </div>
      )}

      <Dialog open={!!receiptFor} onOpenChange={(o) => !o && setReceiptFor(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="no-print">{lang === "es" ? "Recibo de pago" : "Payment receipt"}</DialogTitle></DialogHeader>
          {receiptFor && data && (
            <div className="receipt-print" data-testid="payroll-receipt">
              <div className="flex items-center gap-3 border-b border-slate-200 pb-4">
                {data.business.logo ? <img src={data.business.logo} alt="logo" className="h-12 w-12 object-contain" /> : <span className="flex h-12 w-12 items-center justify-center rounded-sm bg-primary text-white"><HardHat className="h-6 w-6" /></span>}
                <div><p className="font-head text-lg font-bold text-secondary">{data.business.name}</p><p className="text-xs text-slate-400">{lang === "es" ? "Recibo de pago de nómina" : "Payroll payment receipt"}</p></div>
              </div>
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">{lang === "es" ? "Empleado" : "Employee"}</span><b className="text-secondary">{receiptFor.employee_name}</b></div>
                <div className="flex justify-between"><span className="text-slate-500">{lang === "es" ? "Periodo" : "Period"}</span><span className="text-secondary">{data.period.start} → {data.period.end}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">{lang === "es" ? "Tarifa" : "Rate"}</span><span className="text-secondary">${receiptFor.hourly_rate}/h</span></div>
                <div className="flex justify-between"><span className="text-slate-500">{lang === "es" ? "Horas" : "Hours"}</span><span className="text-secondary">{receiptFor.hours}h</span></div>
                {receiptFor.paid_at && <div className="flex justify-between"><span className="text-slate-500">{lang === "es" ? "Pagado el" : "Paid on"}</span><span className="text-secondary">{new Date(receiptFor.paid_at).toLocaleDateString()}</span></div>}
                <div className="mt-2 flex justify-between border-t border-slate-200 pt-3 font-head text-xl font-extrabold text-accent"><span>{lang === "es" ? "Total pagado" : "Total paid"}</span><span data-testid="receipt-amount">${receiptFor.pay.toFixed(2)}</span></div>
              </div>
              <p className="mt-6 text-xs text-slate-400">{lang === "es" ? "Firma de recibido: ____________________" : "Received by: ____________________"}</p>
              <Button onClick={() => window.print()} className="no-print mt-6 w-full rounded-sm bg-secondary hover:bg-slate-700" data-testid="receipt-print-btn"><Printer className="mr-2 h-4 w-4" />{lang === "es" ? "Imprimir / PDF" : "Print / PDF"}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
